import type { DailyRoutineRepository } from '../../../application/ports/DailyRoutineRepository';
import type { DailyRoutine } from '../../../domain/routine/models';
import type { DailyRoutineId, ProfileId } from '../../../domain/shared/ids';
import type { AscendInvictusDatabase } from '../AscendInvictusDatabase';
import { DailyRoutineMapper } from '../mappers';
import { executePersistence } from '../translatePersistenceError';

export class DexieDailyRoutineRepository implements DailyRoutineRepository {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  public save(routine: DailyRoutine): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.dailyRoutines.put(DailyRoutineMapper.toRecord(routine));
    });
  }

  public findById(id: DailyRoutineId): Promise<DailyRoutine | null> {
    return executePersistence('read', async () => {
      const record = await this.database.dailyRoutines.get(id);
      return record ? DailyRoutineMapper.fromRecord(record) : null;
    });
  }

  public findByLogicalDate(
    profileId: ProfileId,
    logicalDate: string,
  ): Promise<DailyRoutine | null> {
    return executePersistence('read', async () => {
      const record = await this.database.dailyRoutines
        .where('[profileId+logicalDate]')
        .equals([profileId, logicalDate])
        .first();
      return record ? DailyRoutineMapper.fromRecord(record) : null;
    });
  }

  public listByProfile(profileId: ProfileId): Promise<readonly DailyRoutine[]> {
    return executePersistence('read', async () => {
      const records = await this.database.dailyRoutines
        .where('profileId')
        .equals(profileId)
        .sortBy('logicalDate');
      return records.map((record) => DailyRoutineMapper.fromRecord(record));
    });
  }

  public listByDateRange(
    profileId: ProfileId,
    fromLogicalDate: string,
    toLogicalDate: string,
  ): Promise<readonly DailyRoutine[]> {
    return executePersistence('read', async () => {
      const records = await this.database.dailyRoutines
        .where('[profileId+logicalDate]')
        .between([profileId, fromLogicalDate], [profileId, toLogicalDate], true, true)
        .sortBy('logicalDate');
      return records.map((record) => DailyRoutineMapper.fromRecord(record));
    });
  }

  public delete(id: DailyRoutineId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.dailyRoutines.delete(id);
    });
  }

  public clearForProfile(profileId: ProfileId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.dailyRoutines.where('profileId').equals(profileId).delete();
    });
  }
}
